import logger from './utils/logger'
import React, {useEffect} from 'react'
import AccessButton from './components/accessButton'
import MainTab from './components/tabs/mainTab'
import TabsComponent from './components/tabs/tabsComponent'
import useCardano from './hooks/cardanoProvider'
import useNetwork, {NETWORK_CARDANO, NETWORK_ETHEREUM} from './hooks/networkProvider'
import BitcoinAccessButton from './components/bitcoinAccessButton'
import BitcoinMainTab from './components/tabs/bitcoinMainTab'
import {CONNECTED, NO_PROVIDER} from './utils/connectionStates'
import {isCip95Tab} from './utils/cip95'
import NetworkToggle from './components/networkToggle'
import EthereumAccessButton from './components/ethereumAccessButton'
import EthereumMainTab from './components/tabs/ethereumMainTab'
import Cip8Tab from './components/tabs/subtabs/cip8Tab'
import Cip30Tab from './components/tabs/subtabs/cip30Tab'
import Cip20Tab from './components/tabs/subtabs/cip20Tab'
import Cip95Tab from './components/tabs/subtabs/cip95Tab'
import Cip95TabTools from './components/tabs/subtabs/cip95ToolsTab'
import NFTTab from './components/tabs/subtabs/NFTTab'
import UtilsTab from './components/tabs/subtabs/utilsTab'
import TokenTab from './components/tabs/subtabs/tokenTab'
import Staking from './components/tabs/subtabs/stakingTab'
import TestTxsTab from './components/tabs/subtabs/testTxsTab'
import Eip1193Tab from './components/tabs/subtabs/eip1193Tab'
import EthTransactionsTab from './components/tabs/subtabs/ethTransactionsTab'
import Erc20Tab from './components/tabs/subtabs/erc20Tab'

const App = () => {
  const {connectionState, selectedWallet, setConnectionState, setConnectionStateFalse, cip95Available} = useCardano()
  const {activeNetwork} = useNetwork()
  const isWalletConnected = connectionState === CONNECTED
  const isNoProvider = connectionState === NO_PROVIDER

  const walletStateWithTimeout = async (walletObject, timeout = 2000) => {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Checking connection timeout'))
      }, timeout)
    })
    const walletEnabledPromise = walletObject.isEnabled()
    const response = await Promise.race([walletEnabledPromise, timeoutPromise])
    return response
  }

  useEffect(() => {
    const getConnectionState = async () => {
      logger.debug(`[dApp][App] Checking connection works`)
      try {
        const walletObject = window.cardano[selectedWallet]
        const conState = await walletStateWithTimeout(walletObject, 10000)

        if (conState) {
          setConnectionState(CONNECTED)
        } else {
          setConnectionStateFalse()
        }
      } catch (error) {
        setConnectionStateFalse()
        logger.error(error)
      }
    }

    if (isWalletConnected) {
      const connectionTimer = setInterval(getConnectionState, 10000)
      return () => {
        logger.debug(`[dApp][App] Checking connection is stopped`)
        clearInterval(connectionTimer)
      }
    }
  }, [isWalletConnected, selectedWallet, setConnectionState, setConnectionStateFalse])

  const mainTabProps = {
    isWalletConnected,
    isNoProvider,
  }

  const cardanoTabsData = [
    {
      label: 'CIP-8',
      value: 'cip8',
      children: <Cip8Tab />,
    },
    {
      label: 'CIP-30',
      value: 'cip30',
      children: <Cip30Tab />,
    },
    {
      label: 'CIP-20',
      value: 'cip20',
      children: <Cip20Tab />,
    },
    {
      label: 'Staking',
      value: 'staking',
      children: <Staking />,
    },
    {
      label: 'CIP-95',
      value: 'cip95',
      children: <Cip95Tab />,
    },
    {
      label: 'CIP-95 Tools',
      value: 'cip95Tools',
      children: <Cip95TabTools />,
    },
    {
      label: 'Tokens',
      value: 'tokens',
      children: <TokenTab />,
    },
    {
      label: 'NFTs',
      value: 'nfts',
      children: <NFTTab />,
    },
    {
      label: 'Utils',
      value: 'utils',
      children: <UtilsTab />,
    },
    {
      label: 'Test Txs',
      value: 'testTxs',
      children: <TestTxsTab />,
    },
  ].filter((tab) => cip95Available || !isCip95Tab(tab.value))

  const ethereumTabsData = [
    {
      label: 'EIP-1193',
      value: 'eip1193',
      children: <Eip1193Tab />,
    },
    {
      label: 'Transactions',
      value: 'ethTx',
      children: <EthTransactionsTab />,
    },
    {
      label: 'ERC-20',
      value: 'erc20',
      children: <Erc20Tab />,
    },
  ]

  const renderNetwork = () => {
    if (activeNetwork === NETWORK_CARDANO) {
      return (
        <>
          <AccessButton />
          <MainTab {...mainTabProps} />
          <TabsComponent tabsData={cardanoTabsData} />
        </>
      )
    }
    if (activeNetwork === NETWORK_ETHEREUM) {
      return (
        <>
          <EthereumAccessButton />
          <EthereumMainTab />
          <TabsComponent tabsData={ethereumTabsData} />
        </>
      )
    }
    return (
      <>
        <BitcoinAccessButton />
        <BitcoinMainTab />
      </>
    )
  }

  return (
    <div className="min-h-screen bg-gray-800">
      <NetworkToggle />
      {renderNetwork()}
    </div>
  )
}

export default App
